import { useMemo } from 'react';
import { identity, pipe } from 'fp-ts/function';
import { array, string } from 'fp-ts';
import { NewRoutingRuleResult, RoutingRuleTriggerType } from '@mews-commander/data-access';
import { TableState } from '@optimus-web/core';
import { BookableService } from '@mews-commander/legacy-entities/src/ducks/servicesNew';
import { RoutingRuleListTableData, RoutingRuleListTableRowData } from '../components/RoutingRulesTable/types';
import {
    filterRoutingRuleTableRow,
    filterRoutingRuleTableRowByGlobalSearch,
} from '../components/RoutingRulesTable/utils/tableData';
import {
    RoutingRulesListFilterSchemaKeys,
    RoutingRulesListFilterValues,
} from '../schemas/routingRulesListFilterSchema';
import { useRoutingRulesData } from '../queries';
import { useServicesSuspenseQuery } from '../components/form/steps/itemsAndConditions/queries';
import { getRoutingRuleServices } from './newRoutingRuleResult';
import { getRoutingRuleConditions } from './conditions';

const mapNewRoutingRuleResultToTableData = (
    result: NewRoutingRuleResult,
    bookableServices: BookableService[]
): RoutingRuleListTableData => {
    if (!result.routingRules) {
        return [];
    }
    return result.routingRules
        .map((routingRule, index) => ({
            id: routingRule.id,
            createdUtc: routingRule.createdUtc,
            active: false,
            name: routingRule.name,
            conditions: getRoutingRuleConditions(result, index),
            services: getRoutingRuleServices(result, index, bookableServices),
            targetBill: {
                targetType: routingRule.assignmentTargetType,
                aggregationType: routingRule.billAggregationType,
            },
            timing: routingRule.triggerType,
            processingStartOffset: routingRule.processingStartOffset,
        }))
        .sort((a, b) => string.Ord.compare(b.createdUtc, a.createdUtc));
};

export const useRoutingRuleTableData = (
    filters: RoutingRulesListFilterValues | null,
    tableState: TableState<RoutingRuleListTableRowData>
) => {
    const { search } = tableState.globalFilters;

    const query = useRoutingRulesData();
    const {
        data: { bookableServices },
    } = useServicesSuspenseQuery();

    const tableData = mapNewRoutingRuleResultToTableData(query.data ?? {}, bookableServices);

    const { filteredData, displayLegacy } = useMemo(
        () => ({
            filteredData: pipe(
                tableData,
                array.filter(
                    filterRoutingRuleTableRow({
                        name: filters?.[RoutingRulesListFilterSchemaKeys.Name],
                        timings: filters?.[RoutingRulesListFilterSchemaKeys.Timing],
                        company: filters?.[RoutingRulesListFilterSchemaKeys.Company],
                        travelAgency: filters?.[RoutingRulesListFilterSchemaKeys.TravelAgency],
                    })
                ),
                search ? array.filter(filterRoutingRuleTableRowByGlobalSearch(search)) : identity
            ),
            displayLegacy: !!query.data?.routingRules?.some((row) => row.triggerType === RoutingRuleTriggerType.Legacy),
        }),
        [filters, tableData, query.data, search]
    );

    return useMemo(
        () => ({
            isFetching: query.isFetching,
            data: filteredData,
            displayLegacy,
        }),
        [displayLegacy, filteredData, query.isFetching]
    );
};
